import type { ChurchMembershipSummary } from '@/stores/church-store';

type ChurchCampusFields = Pick<
  { campusName: string; parentChurchId: string | null; id: string },
  'campusName' | 'parentChurchId' | 'id'
>;

/** Label for the campus someone is viewing in a multi-campus church. */
export const campusInViewLabel = (
  church: Pick<{ campusName: string; parentChurchId: string | null }, 'campusName' | 'parentChurchId'>,
  fallbackLabel: string,
): string => church.campusName?.trim() || fallbackLabel;

/** Organisation name shown beside the campus in the top bar. */
export const churchOrgName = (
  church: Pick<{ name: string }, 'name'>,
  headOffice: Pick<{ name: string }, 'name'> | null,
): string => headOffice?.name ?? church.name;

/** Head church for a branch tenant, resolved from stored context or memberships. */
export const resolveHeadOffice = (
  church: ChurchCampusFields | null,
  headOffice: Pick<{ id: string; name: string }, 'id' | 'name'> | null,
  memberships: ChurchMembershipSummary[],
): Pick<{ id: string; name: string }, 'id' | 'name'> | null => {
  if (headOffice) return headOffice;
  if (!church?.parentChurchId) return null;

  const headMembership = memberships.find((row) => row.church.id === church.parentChurchId);
  return headMembership
    ? { id: headMembership.church.id, name: headMembership.church.name }
    : null;
};

/**
 * Whether the top bar should show which campus is in context.
 *
 * Always labelled so staff always know which branch they are working in. Switching
 * between campuses stays gated on the Branches module and plan.
 */
export const shouldShowCampusInTopBar = (
  _church: ChurchCampusFields,
  _memberships: ChurchMembershipSummary[],
): boolean => true;
